/** @jsx jsx */
import React from 'react';
import { isIPad13, isTablet } from "react-device-detect";
import { Column } from 'react-table';
import { css, jsx } from '@emotion/core';
import { utilityColor } from 'components/styles';
import DataTable from 'components/organismos/DataTable/DataTable';
import StaffDomain from 'domain/staff';
import ButtonNavigation from './ButtonNavigation';
import { useDownload } from './hooks';
import OrganizationFilter from './OrganizationFilter';
import EmploymentFilter from './EmploymentFilter';
import ExecutiveFilter from './ExecutiveFilter';
import StateStatusFilter from './StateStatusFilter';
import FormContentFilter from 'components/atoms/Form/FormContentFilter';
import SubActionButton from 'components/atoms/Button/SubActionButton';
import { useHistory } from 'react-router-dom';
import FormContents from 'components/atoms/Form/FormContents';
import FormHeader from 'components/templates/FormHeader';

const styles = {
  bgWhite: css({
    backgroundColor: utilityColor.white,
    border: '1px solid #dcdcdc'
  }),
  flexboxCenter: css({
    textAlign: 'center',
    justifyContent: 'center'
  }),
  table: css(
    {},
    css`
      table tr th:nth-child(1),
      table tr td:nth-child(1) {
        width: 50px;
        text-align:center;
        white-space: nowrap;
      }
      table tr th:nth-of-type(2) {
        width: 80px;
        white-space: pre-line;
      }
      table th:nth-of-type(2) span {
        display: block;
        margin: auto;
      }
      table th:nth-of-type(2) span svg {
        margin-left: 0!important;
      }
      }
      table tr th:nth-of-type(3) {
        width: 140px;
      }
      table tr th:nth-of-type(4) {
        width: 80px;
        white-space: pre-line;
      }
      table th:nth-of-type(4) span {
        display: block;
        margin: auto;
      }
      table th:nth-of-type(4) span svg {
        margin-left: 0!important;
      }
      table tr th:nth-of-type(5) {
        width: 145px;
      }
      table tr th:nth-of-type(6) {
        width: 70px;
        white-space: pre-line;
      }
      table th:nth-of-type(6) span {
        display: block;
        margin: auto;
      }
      table th:nth-of-type(6) span svg {
        margin-left: 0!important;
      }
      table tr th:nth-of-type(7) {
        width: 145px;
      }
      table tr th:nth-of-type(8) {
        width: 80px;
        white-space: pre-line;
      }
      table th:nth-of-type(8) span {
        display: block;
        margin: auto;
      }
      table th:nth-of-type(8) span svg {
        margin-left: 0!important;
      }
      table tr th:nth-of-type(9) {
        width: 145px;
      }
      table tr th:nth-of-type(10) {
        width: 60px;
      }
    `
  ),
  searchBox: css(
    {
      zIndex: 'revert'
    }, 
    css`
      z-index:1;
    `
  )
};

const StaffListTable: React.FC<{
  staffList: Array<StaffDomain>;
  fetchData: () => Promise<void>;
  executiveId: string;
  setExecutiveId: (arg: string) => void;
  employmentId: string;
  setEmploymentId: (arg: string) => void;
  orgCode: string;
  setOrgCode: (arg: string) => void;
  stateStatus: Array<number>;
  setStateStatus: (arg: Array<number>) => void;
}> = ({
  staffList, fetchData,
  executiveId, setExecutiveId,
  employmentId, setEmploymentId,
  orgCode, setOrgCode,
  stateStatus, setStateStatus
}) => {
  const history = useHistory();

  const {
    downloadStaffCsv, 
    downloadStaffManagerCsv, 
    downloadStaffSalaryCsv, 
    downloadStaffHolidayCsv
  } = useDownload(orgCode, executiveId, employmentId, stateStatus);

  const columns: Array<Column<StaffDomain>> = React.useMemo(() => [
    {
      Header: () => null,
      accessor: 'orgCode',
      id: 'actionButton',
      disableSortBy: true,
      Cell: (cell: { row: { isExpanded: boolean, values: StaffDomain } }) => {
        return (
          <SubActionButton 
            action={0} 
            text="編集" 
            onClick={() => {
              history.push(`/staffs/${cell.row.values.staffCode}`);
            }} 
          />
        );
      }
    },
    {
      Header: '組織\nコード',
      accessor: 'orgCode'
    },
    {
      Header: '組織名',
      accessor: 'orgName'
    },
    {
      Header: 'スタッフ\nコード',
      accessor: 'staffCode'
    },
    {
      Header: 'スタッフ名',
      accessor: 'staffName'
    },
    {
      Header: '役職\nコード',
      accessor: 'executiveCode'
    },
    {
      Header: '役職名',
      accessor: 'executiveName'
    },
    {
      Header: '雇用形態\nコード',
      accessor: 'employmentCode'
    },
    {
      Header: '雇用形態',
      accessor: 'employmentName'
    },
    {
      Header: '状態',
      accessor: 'stateStatusDisp'
    },
    {
      Header: '',
      accessor: 'mail',
      show: false
    },
  ], [history]);

  return (
    <div>
      <FormHeader title="スタッフマスタ" />
      <FormContents customStyle={css({marginTop:'60px'})}>
        <FormContentFilter customStyles={css({ padding: '7px' })}>
          <OrganizationFilter
            orgCode={orgCode}
            setOrgCode={(arg: string) => {
              setOrgCode(arg);
            }}
            defaultValue={sessionStorage.getItem('loginUser.orgCode')}
          />

          <ExecutiveFilter
            executiveId={executiveId}
            setExecutiveId={(arg: string) => {
              setExecutiveId(arg);
            }}
          />

          <EmploymentFilter
            employmentId={employmentId}
            setEmploymentId={(arg: string) => {
              setEmploymentId(arg);
            }}
          />

          <StateStatusFilter
            stateStatus={stateStatus}
            setStateStatus={(arg: Array<number>) => {
              setStateStatus(arg);
            }}
            defaultValue={[1, 3, 4]}
          />
        </FormContentFilter>
        <div>
          <ButtonNavigation
            downloadStaffCsv={downloadStaffCsv}
            downloadStaffSalaryCsv={downloadStaffSalaryCsv}
            downloadStaffManagerCsv={downloadStaffManagerCsv}
            downloadStaffHolidayCsv={downloadStaffHolidayCsv}
          />
          <div 
            className="table-padding table-bordered" 
            css={styles.table} 
            style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '15px' }}
          >
            <DataTable
              classNameParrant="border-left-right"
              className="table-no-border-left-right" 
              columns={columns}
              data={staffList}
              isGlobalFilter={true}
              minWidth="100%"
              globalSearchStyle={styles.searchBox}
            />
          </div>
        </div>
      </FormContents>
    </div>
  );
};

export default StaffListTable;
